import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { inputCls } from "@/components/crm/ui";
import { MEDIA } from "@/config/media";


export default function Login() {
  const { login, user, booted } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "BRICKSPACE | Advisor Login";
    if (booted && user) navigate("/crm", { replace: true });
  }, [booted, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate("/crm", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-white lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center justify-center px-5 py-14">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <Link
            to="/"
            data-testid="back-to-site"
            className="flex items-center gap-2 text-xs font-semibold text-body hover:text-brand"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to website
          </Link>

          <div className="mt-10 font-display text-xl font-extrabold tracking-[-0.03em] text-ink">
            brickspace<span className="text-brand">.</span>
          </div>
          <h1 className="mt-6 font-display text-3xl font-extrabold text-ink">Advisor sign in</h1>
          <p className="mt-2 text-sm text-body">Owners and property advisors only.</p>

          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="login-form">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Work email"
              data-testid="login-email-input"
              className={inputCls}
            />
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="login-password-input"
              className={inputCls}
            />
            {error && (
              <p
                data-testid="login-error"
                className="rounded-xl bg-red-50 px-3.5 py-2.5 text-xs font-medium text-red-600 ring-1 ring-red-100"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 text-sm font-bold text-white transition-colors hover:bg-brandDark disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Signing in" : "Sign in"}
            </button>
          </form>

          <p className="mt-8 text-xs text-muted2">
            Access is issued by the BRICKSPACE owner. Lost your password? Ask the owner to reset it
            from the Team page.
          </p>
        </motion.div>
      </div>

      <div className="relative hidden overflow-hidden bg-ink lg:block">
        <img src={MEDIA.hero} alt="BRICKSPACE" className="h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-12">
          <h2 className="max-w-md font-display text-4xl font-extrabold leading-tight text-white">
            The lead console for the <span className="text-brand">NH-24 corridor</span>
          </h2>
          <p className="mt-4 max-w-sm text-sm text-white/70">
            Call, WhatsApp, follow up and close — from your phone or your desk.
          </p>
        </div>
      </div>
    </div>
  );
}
